import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { CreditCard, Plus, Trash2, AlertCircle } from 'lucide-react';

/**
 * Manager/Director section to pair and manage Linkly Cloud EFTPOS terminals.
 *
 * Visible only when `/api/linkly/health` returns enabled=true. Until the
 * merchant sets LINKLY_ENABLED + the two URLs + a webhook secret in the
 * backend env, this section is hidden by the parent.
 *
 * Flow per Linkly docs:
 *   1. Bartender presses Function on Move5000, terminal shows a 6-digit
 *      pair code.
 *   2. Manager fills in username + password (Linkly Cloud creds, one per
 *      terminal) + the pair code + a friendly label.
 *   3. Hit Pair — backend exchanges with Linkly, stores the returned
 *      `Secret`, surfaces the terminal here.
 */
export default function LinklyTerminals() {
  const [terminals, setTerminals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ username: '', password: '', pair_code: '', label: '' });

  const refresh = async () => {
    try {
      const { data } = await api.get('/linkly/terminals');
      setTerminals(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load terminals');
    }
  };

  useEffect(() => { refresh(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.username || !form.password || !form.pair_code || !form.label) {
      toast.error('Fill all fields');
      return;
    }
    setLoading(true);
    try {
      await api.post('/linkly/terminals/pair', form);
      toast.success(`Paired · ${form.label}`);
      setShowForm(false);
      setForm({ username: '', password: '', pair_code: '', label: '' });
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Pairing failed');
    } finally {
      setLoading(false);
    }
  };

  const unpair = async (id, label) => {
    if (!window.confirm(`Unpair ${label}? You'll need to re-enter Cloud creds + a new pair code to re-add it.`)) return;
    try {
      await api.delete(`/linkly/terminals/${id}`);
      toast.success('Unpaired');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  return (
    <div className="bg-white/[0.02] border border-white/10 rounded-sm p-4 space-y-3" data-testid="linkly-terminals-section">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CreditCard className="w-4 h-4 text-gold" />
          <h3 className="text-white font-serif text-base">EFTPOS Terminals</h3>
          <span className="text-zinc-500 text-xs font-sans">· {terminals.length} paired</span>
        </div>
        <Button
          onClick={() => setShowForm(v => !v)}
          size="sm"
          className="h-8 bg-gold/15 text-gold border border-gold/30 hover:bg-gold/25 rounded-sm font-sans text-xs"
          data-testid="linkly-add-terminal"
        >
          <Plus className="w-3.5 h-3.5 mr-1" /> {showForm ? 'Cancel' : 'Pair terminal'}
        </Button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="rounded-sm border border-gold/20 bg-gold/5 p-3 space-y-3" data-testid="linkly-pair-form">
          <p className="text-xs text-zinc-300 font-sans leading-relaxed">
            On the Move5000: press <kbd className="px-1 bg-white/10 rounded-sm">Func</kbd>, navigate to <em>Pair POS</em>, and copy the 6-digit code shown.
          </p>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 font-sans">Linkly Cloud Username</Label>
              <Input value={form.username} onChange={e => setForm({ ...form, username: e.target.value })} autoComplete="off" className="h-8 bg-transparent border-white/10 text-white font-sans text-sm" data-testid="linkly-username" />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 font-sans">Linkly Cloud Password</Label>
              <Input value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} type="password" autoComplete="off" className="h-8 bg-transparent border-white/10 text-white font-sans text-sm" data-testid="linkly-password" />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 font-sans">Pair Code (6 digits)</Label>
              <Input value={form.pair_code} onChange={e => setForm({ ...form, pair_code: e.target.value })} maxLength={6} className="h-8 bg-transparent border-white/10 text-white font-sans text-sm font-mono" data-testid="linkly-pair-code" />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 font-sans">Label</Label>
              <Input value={form.label} onChange={e => setForm({ ...form, label: e.target.value })} placeholder="e.g. Main Bar Move5000" className="h-8 bg-transparent border-white/10 text-white font-sans text-sm" data-testid="linkly-label" />
            </div>
          </div>
          <Button type="submit" disabled={loading} className="w-full h-9 bg-gold text-black hover:bg-gold-light font-sans text-sm font-bold disabled:opacity-50" data-testid="linkly-pair-submit">
            {loading ? 'Pairing…' : 'Pair Terminal'}
          </Button>
          <p className="text-[10px] text-zinc-500 font-sans flex items-start gap-1">
            <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" />
            Username + password are not stored after pairing — only the returned secret is kept (encrypted at rest is a P0 production TODO).
          </p>
        </form>
      )}

      {terminals.length === 0 ? (
        <p className="text-sm text-zinc-500 font-sans italic py-2">No terminals paired yet.</p>
      ) : (
        <div className="space-y-1.5">
          {terminals.map(t => (
            <div key={t.id} className="flex items-center justify-between rounded-sm border border-white/10 bg-white/[0.02] px-3 py-2" data-testid={`linkly-terminal-${t.id}`}>
              <div>
                <p className="text-white font-sans text-sm">{t.label}</p>
                <p className="text-zinc-500 font-sans text-xs">{t.cloud_username} · paired {new Date(t.paired_at).toLocaleDateString()}</p>
              </div>
              <button
                onClick={() => unpair(t.id, t.label)}
                className="text-red-400 hover:text-red-300 hover:bg-red-500/10 w-8 h-8 rounded-sm flex items-center justify-center"
                data-testid={`linkly-unpair-${t.id}`}
                title="Unpair"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
